import { useMemo } from 'react';
import { Select, Button, Tooltip } from 'antd';
import { SearchOutlined, NodeIndexOutlined } from '@ant-design/icons';
import { listSearchableAccounts, SECTIONS } from '../../utils/accountStructure/accountSearch';
import './styles.scss';

const { OptGroup, Option } = Select;

/**
 * "Find account" box. Picking an account calls `onLocate(account)`; the page
 * switches tab and the table expands, scrolls to and highlights the row.
 * With `onShowWhereUsed`, a button next to it opens "Where used" for the
 * account in `value`.
 *
 * Controlled: `value` is the identity of the last located account, kept by
 * the page because the box remounts when the tab changes.
 */
const AccountSearch = ({ accountStructure, value, onLocate, onClear, onShowWhereUsed, style }) => {
    const entries = useMemo(() => listSearchableAccounts(accountStructure), [accountStructure]);
    const byIdentity = useMemo(() => new Map(entries.map((e) => [e.identity, e])), [entries]);
    // Looked up on each render so edits to the account (or its removal) show.
    const selected = byIdentity.get(value) ?? null;

    const groups = useMemo(
        () =>
            SECTIONS.map((section) => ({
                section,
                entries: entries.filter((e) => e.section === section),
            })).filter((g) => g.entries.length > 0),
        [entries],
    );

    const filterOption = (input, option) => {
        const search = input.trim().toLowerCase();
        if (!search) return true;
        return (option?.['data-search'] || '').includes(search);
    };

    const handleSelect = (identity) => {
        const entry = byIdentity.get(identity);
        if (entry) onLocate?.(entry.account);
    };

    return (
        <div className="account-search" style={style}>
            <Select
                showSearch
                allowClear
                value={selected?.identity ?? null}
                placeholder={
                    <>
                        <SearchOutlined /> Find account (number or name)
                    </>
                }
                filterOption={filterOption}
                onSelect={handleSelect}
                onClear={onClear}
                optionLabelProp="label"
                dropdownMatchSelectWidth={420}
                listHeight={360}
                className="account-search-select"
            >
                {groups.map(({ section, entries: sectionEntries }) => (
                    <OptGroup key={section.key} label={section.label}>
                        {sectionEntries.map((e) => (
                            <Option
                                key={e.identity}
                                value={e.identity}
                                label={`${e.account.account_number} ${e.account.name ?? ''}`.trim()}
                                data-search={`${e.account.account_number} ${e.account.name ?? ''}`.toLowerCase()}
                            >
                                <span className="account-search-number">{e.account.account_number}</span>
                                {e.account.name && <span className="account-search-name">{e.account.name}</span>}
                                {e.path.length > 0 && (
                                    <div className="account-search-path">{e.path.join(' › ')}</div>
                                )}
                            </Option>
                        ))}
                    </OptGroup>
                ))}
            </Select>
            {onShowWhereUsed && (
                <Tooltip title={selected ? `Where is ${selected.account.account_number} used?` : 'Find an account first'}>
                    <Button
                        icon={<NodeIndexOutlined />}
                        disabled={!selected}
                        onClick={() => onShowWhereUsed(selected.account)}
                    >
                        Where used
                    </Button>
                </Tooltip>
            )}
        </div>
    );
};

export default AccountSearch;
